import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { FileText, Paperclip, X } from "lucide-react";
import { validateForumFiles } from "../../services/moodle/forum";
import { attachmentExtension, formatFileSize } from "./attachmentUtils";
import { useTheme } from "../../contexts/ThemeContext";
import { CsvPreview } from "./AttachmentGallery";
import { OfficePreview } from "./OfficePreview";
import { previewHeaderTheme } from "./previewHeaderTheme";

const useLocalUrl = (file) => {
  const [current, setCurrent] = useState(null);
  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setCurrent({ file, url });
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return current?.file === file ? current.url : null;
};

const LocalThumbnail = ({ file }) => {
  const image = ["jpg", "jpeg", "png"].includes(attachmentExtension(file));
  const url = useLocalUrl(image ? file : null);
  return url ? <img src={url} alt="" /> : <FileText size={24} aria-hidden="true" />;
};

const LocalPreview = ({ file }) => {
  const extension = attachmentExtension(file);
  const image = ["jpg", "jpeg", "png"].includes(extension);
  const url = useLocalUrl(image || extension === "pdf" ? file : null);
  if (image) return url && <img className="forum-local-preview-image" src={url} alt={file.name} />;
  if (extension === "pdf") return url && <iframe title={`Vista previa: ${file.name}`} src={url} sandbox="allow-scripts" />;
  if (extension === "csv") return <CsvPreview blob={file} />;
  if (["docx", "xlsx"].includes(extension)) return <OfficePreview blob={file} extension={extension} filename={file.name} unpublished />;
  return <p>No hay vista previa disponible para este archivo.</p>;
};

export const AttachmentPicker = ({ files, onChange, policy, disabled, progress, showLimits = true }) => {
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const theme = useTheme();
  const select = (event) => {
    const selected = [...files, ...Array.from(event.target.files || [])];
    event.target.value = "";
    try {
      validateForumFiles(selected, policy);
      onChange(selected);
      setError("");
    } catch (failure) {
      setError(failure.message);
    }
  };
  return (
    <div className="forum-attachment-picker" data-theme={theme.mode === "dark" ? "dark" : "light"}>
      <label className="forum-attachment-add">
        <Paperclip size={18} aria-hidden="true" /> <span>Adjuntar archivos</span>
        <input aria-label="Adjuntar archivos" type="file" multiple
          accept={policy?.extensions.map((extension) => `.${extension}`).join(",") || ""}
          onChange={select} disabled={disabled || !policy} />
      </label>
      {!policy ? <small role="status">Cargando límites de Moodle…</small> : showLimits && <small>{`Máximo ${policy.maxfiles} archivos · ${formatFileSize(policy.maxbytes)} por archivo`}</small>}
      {files.length > 0 && <ul className="forum-attachment-selection">{files.map((file, index) => (
        <li key={`${file.name}-${index}`}>
          <button type="button" className="forum-local-file" aria-label={`Vista previa de ${file.name}`} onClick={() => setPreview(file)}>
            <span className="forum-local-thumbnail"><LocalThumbnail file={file} /></span>
            <span className="forum-local-file-info"><strong>{file.name}</strong><small>{formatFileSize(file.size)}</small></span>
          </button>
          <button type="button" aria-label={`Retirar ${file.name}`} disabled={disabled}
            onClick={() => { if (preview === file) setPreview(null); onChange(files.filter((_, current) => current !== index)); }}><X size={16} /></button>
        </li>
      ))}</ul>}
      <Dialog open={Boolean(preview && files.includes(preview))} onClose={() => setPreview(null)} fullWidth maxWidth="lg" aria-labelledby="forum-local-preview-title" PaperProps={{ className: "forum-attachment-dialog" }}>
        {preview && files.includes(preview) && <>
          <header className="forum-attachment-toolbar" style={previewHeaderTheme(theme)}><div><strong id="forum-local-preview-title" title={preview.name}>{preview.name}</strong></div>
            <button type="button" aria-label="Cerrar vista previa" onClick={() => setPreview(null)}><X size={20} /></button>
          </header>
          <div className="forum-attachment-preview"><LocalPreview key={preview.name} file={preview} /></div>
        </>}
      </Dialog>
      {disabled && files.length > 0 && <div role="status">{progress < 100 ? `Subiendo archivos… ${progress || 0}%` : "Publicando mensaje…"}</div>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
};
